import {
  deleteDicomNode,
  getDicomNode,
  patchDicomNode,
  postDicomNode,
} from '@api/dicomnode';
import { getPacsEcho, getPacsQuery, getPacsRetrieve } from '@api/pacs';
import { AUTH_ERROR_MESSAGE } from 'config/api';
import { configStore } from 'config/configStore';
import useAuth from '@hooks/Auth/useAuth';
import { usePermissionMutation } from '@hooks/Permission/usePermissionMutation';
import useModalActions from '@hooks/common/useModalAction';
import { APIError } from '@models/index';
import {
  DicomNodeParams,
  DicomNodeParamsWithoutId,
  PacsParams,
} from '@models/pacs';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { isAuthError } from '@api/api';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePacsRetrieveProps {
  dicomNode: string;
  seriesUID: string;
}

interface UsePacsQueryProps {
  params: PacsParams;
  isStudy: boolean;
}

export default function useGetDicomNodes() {
  const { accessToken, refreshTokens } = useAuth();
  const { errorAction } = useModalActions();
  const queryClient = useQueryClient();

  const showError = async (error: APIError) => {
    if (isAuthError(error)) {
      await errorAction(error, AUTH_ERROR_MESSAGE, refreshTokens);
      queryClient.invalidateQueries({ queryKey: ['dicomNodes'] });
      return;
    }

    if (error.response?.status === 422) {
      await errorAction(error, "Can't fetch list");
      return;
    }

    await errorAction(error, "Can't fetch list");
  };

  const query = useQuery({
    queryKey: ['dicomNodes'],
    queryFn: async () => {
      const nodes = await getDicomNode({ accessToken }).then((res) => res.data);
      const results = await Promise.all(
        nodes.map(async (node: DicomNodeParams) => {
          return { ...node, protocol: 'C-MOVE', status: 'unknown' };
        }),
      );
      return results;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.error) {
      showError(query.error as APIError);
    }
  }, [query.error]);

  return query;
}

export function useGetEchos(nodes: DicomNodeParams[] | undefined) {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['Echos', JSON.stringify(nodes)],
    queryFn: async () => {
      if (!nodes) return [];
      const results = await Promise.all(
        nodes.map(async (node) => {
          const res = await getPacsEcho({ accessToken, dicomNode: node.id });
          if (res.status === 200 || res.status === 201) {
            return { ...node, protocol: 'C-MOVE', status: 'success' as const };
          } else {
            return { ...node, protocol: 'C-MOVE', status: 'fail' as const };
          }
        }),
      );
      return results;
    },
    enabled: !!nodes,
    refetchOnWindowFocus: true,
    staleTime: 0,
    networkMode: 'always',
  });
}

export function usePostDicomNode() {
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();

  return useMutation({
    mutationFn: (params: DicomNodeParamsWithoutId) =>
      postDicomNode({ accessToken, params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicomNodes'] });
    },
    onError: (error) => {
      console.error('생성 실패:', error);
    },
  });
}

export function usePatchDicomNode() {
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();

  return useMutation({
    mutationFn: ({
      node,
      nodeId,
    }: {
      node: DicomNodeParamsWithoutId;
      nodeId: string;
    }) =>
      patchDicomNode({
        accessToken,
        params: node,
        id: nodeId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicomNodes'] });
    },
    onError: (error) => {
      console.error('패치 실패:', error);
    },
  });
}

export function useDeleteDicomNode() {
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const { selectAction, errorAction } = useModalActions();

  return usePermissionMutation<void, string>({
    mutationFn: async (id: string) => {
      return new Promise<void>((resolve, reject) => {
        selectAction(
          'Are you sure you want to delete this DicomNode?',
          async () => {
            try {
              await deleteDicomNode({ accessToken, id });
              let dsdata = [...configStore.get().defaultServer];
              if (dsdata.length > 0) {
                for (let i = 0; i < dsdata.length; i++) {
                  const def = dsdata[i];
                  if (def.id === id) {
                    dsdata.splice(i, 1);
                    break;
                  }
                }
              }
              configStore.setData({ defaultServer: dsdata });
              resolve();
            } catch (err) {
              reject(err);
            }
          },
        );
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicomNodes'] });
    },
    onError: (error) => {
      errorAction(
        error as APIError,
        'Failed. Please contact the administrator.',
      );
    },
  });
}

export function useGetEcho() {
  const { accessToken } = useAuth();
  const { errorAction, successAction } = useModalActions();

  return useMutation({
    mutationFn: async (id: string) => {
      return await getPacsEcho({ accessToken, dicomNode: id });
    },
    onSuccess: (response) => {
      successAction(`${response.data.data}`);
    },
    onError: (error) => {
      errorAction(
        error as APIError,
        'Failed. Please contact the administrator.',
      );
    },
  });
}

// export function useGetPacsQuery({ dicomNode, params, isStudy }: UsePacsQueryProps) {
//   const { accessToken, refreshTokens } = useAuth();
//   const { errorAction } = useModalActions();
//   const queryClient = useQueryClient();

//   const showError = async (error: APIError) => {
//     if (isAuthError(error)) {
//       await errorAction(error, AUTH_ERROR_MESSAGE, refreshTokens);
//       queryClient.invalidateQueries({ queryKey: ["pacsQuery"] });
//       return;
//     }
//     await errorAction(error, "Can't fetch PACS query");
//   };

//   const query = useQuery({
//     queryKey: ["pacsQuery", params, isStudy],
//     queryFn: () =>
//       getPacsQuery({ accessToken, params, isStudy }).then((res) => res.data),
//     refetchOnWindowFocus: true,
//     staleTime: 0,
//   });

//   useEffect(() => {
//     if (query.error) {
//       showError(query.error as APIError);
//     }
//   }, [query.error]);

//   return query;
// }

export function useGetPacsQuery() {
  const { accessToken, refreshTokens } = useAuth();
  const { errorAction } = useModalActions();

  const [data, setData] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);

  const fetchData = useCallback(
    async (params: PacsParams, isStudy: boolean) => {
      setIsFetching(true);
      try {
        const res = await getPacsQuery({ accessToken, params, isStudy });
        if (res.status === 200 || res.status === 201) {
          setData(res.data);
          return res.data;
        }

        console.log('Axios Error ',res.status);
      } catch (err) {
        await errorAction(err as APIError, "Can't fetch PACS query", undefined);
      } finally {
        setIsFetching(false);
      }
    },
    [accessToken, refreshTokens, errorAction],
  );

  return { data, isFetching, fetchData };
}

export function useGetPacsRetrieve() {
  const { accessToken, refreshTokens } = useAuth();
  const {
    closeLoadingModal,
    errorAction,
    successAction,
    waitingAction,
  } = useModalActions();

  const [data, setData] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(
    async ({ dicomNode, seriesUID }: UsePacsRetrieveProps) => {
      if (isFetchingRef.current) {
        return undefined;
      }

      isFetchingRef.current = true;
      setIsFetching(true);
      waitingAction({
        type: 'download',
        message: 'Retrieving PACS series',
      });

      try {
        const res = await getPacsRetrieve({
          accessToken,
          dicomNode,
          seriesUID,
        });
        if (res.status !== 200 && res.status !== 201) {
          throw res;
        }

        setData(res.data);
        closeLoadingModal();
        await successAction('Download Success');
        return res.data;
      } catch (err) {
        const apiError = err as APIError;
        closeLoadingModal();
        if (isAuthError(apiError)) {
          await errorAction(apiError, AUTH_ERROR_MESSAGE, refreshTokens);
        } else {
          await errorAction(apiError, "Can't fetch PACS retrieve");
        }
        throw apiError;
      } finally {
        isFetchingRef.current = false;
        setIsFetching(false);
      }
    },
    [
      accessToken,
      closeLoadingModal,
      errorAction,
      refreshTokens,
      successAction,
      waitingAction,
    ],
  );

  return { data, isFetching, fetchData };
}

// export function useGetPacsRetrieve({ dicomNode, seriesUID }: UsePacsRetrieveProps) {
//   const { accessToken, refreshTokens } = useAuth();
//   const { errorAction } = useModalActions();
//   const queryClient = useQueryClient();

//   const showError = async (error: APIError) => {
//     if (isAuthError(error)) {
//       await errorAction(error, AUTH_ERROR_MESSAGE, refreshTokens);
//       queryClient.invalidateQueries({ queryKey: ["pacsRetrieve"] });
//       return;
//     }
//     await errorAction(error, "Can't fetch PACS retrieve");
//   };

//   const query = useQuery({
//     queryKey: ["pacsRetrieve", dicomNode, seriesUID],
//     queryFn: () =>
//       getPacsRetrieve({ accessToken, dicomNode, seriesUID }).then((res) => res.data),
//     refetchOnWindowFocus: true,
//     staleTime: 0,
//     enabled: false,
//   });

//   useEffect(() => {
//     if (query.error) {
//       showError(query.error as APIError);
//     }
//   }, [query.error]);

//   return query;
// }
